<template>
    <b-container id="supplement-review-list-page" fluid>
        <b-row>
            <b-col cols="12">
                <admin-page-header title="AI 보완 검토" description="프로젝트·업체 단위로 자료보완 필요 여부를 초벌 판정하고 보완요청 메일 초안을 만듭니다."/>
            </b-col>
        </b-row>

        <!-- 검색/필터 -->
        <b-card no-body bg-variant="white" class="mb-3">
            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <h5 class="mb-0">보완 검토 대상 검색</h5>
                    <div v-if="loading" class="d-flex align-items-center" style="height: 36px">
                        <b-spinner class="mr-2" variant="primary" style="height:1rem; width:1rem;"/>
                        <span>조회중...</span>
                    </div>
                </div>
            </template>
            <div class="p-3">
                <b-row>
                    <b-col cols="12" md="4" xl="3" class="mb-3 mb-xl-0">
                        <b-form-group label="평가 기간" class="mb-0">
                            <b-form-select v-model="selectedPeriod" :disabled="loading"
                                           @change="onPeriodChange">
                                <b-form-select-option v-for="p in periods" :key="p" :value="p">
                                    {{ p }}
                                </b-form-select-option>
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="8" xl="5" class="mb-3 mb-xl-0">
                        <b-form-group label="프로젝트 (캠페인)" class="mb-0">
                            <b-form-select v-model="selectedProject" :disabled="loading || !projectOptions.length"
                                           @change="onProjectChange">
                                <b-form-select-option :value="null" disabled>
                                    {{ projectOptions.length ? '프로젝트를 선택하세요'
                                        : loadError === 'projects' ? '프로젝트 목록을 불러오지 못했습니다' : '해당 기간에 프로젝트가 없습니다' }}
                                </b-form-select-option>
                                <b-form-select-option v-for="opt in projectOptions"
                                                      :key="opt.project_code"
                                                      :value="opt">
                                    {{ opt.project_code }} · {{ opt.client || '-' }}
                                </b-form-select-option>
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="8" xl="4">
                        <b-form-group label="업체명 / 업체코드 검색" class="mb-0">
                            <b-form-input v-model="companyFilter" :disabled="loading"
                                          placeholder="업체명 또는 업체코드 일부"
                                          debounce="300" trim/>
                        </b-form-group>
                    </b-col>
                </b-row>
            </div>
        </b-card>

        <!-- 목록 -->
        <b-card no-body :bg-variant="rows.length ? 'white' : 'light'" class="list-container">
            <template #header>
                <div class="d-flex align-items-center justify-content-between flex-wrap"
                     style="gap: .5rem">
                    <h6 class="mb-0">
                        보완 검토 대상 업체
                        <span v-if="selectedProject" class="text-muted small ml-2">
                            (미신청 업체 제외 —
                            <template v-if="companyFilter">검색 결과 {{ filteredRows.length }} / 전체 {{ eligibleRows.length }}개</template>
                            <template v-else>총 {{ filteredRows.length }}개</template>)
                        </span>
                    </h6>
                    <!-- selectMode 조건: 필터로 목록이 비어도 종료 버튼은 남긴다 -->
                    <div v-if="selectedProject && (filteredRows.length || selectMode)"
                         class="d-flex align-items-center flex-wrap" style="gap: .5rem">
                        <!-- 진행 중 잡이 있으면 15초마다 자동 갱신(pollJobs) -->
                        <span v-if="jobsUpdatedAt" class="text-muted small">마지막 갱신 {{ jobsUpdatedAt }}</span>
                        <b-button size="sm" variant="outline-secondary" :disabled="jobsLoading"
                                  title="보완검토 상태를 다시 불러옵니다"
                                  @click="loadJobs()">
                            새로고침
                        </b-button>
                        <span v-if="hasTruncatedHistory" class="text-warning small"
                              title="잡 이력이 캠페인당 조회 상한(50건)을 초과했습니다. 이력 확인 불가 업체는 전체 요청 대상에서 제외되며, 각 행의 상세 보기에서 개별 확인할 수 있습니다.">
                            이력 확인 불가 업체는 전체 요청에서 제외됨
                        </span>
                        <b-button size="sm" variant="primary"
                                  :disabled="!noHistoryTargets.length || bulkRequesting || jobsLoading"
                                  @click="requestAllNoHistory">
                            <b-spinner v-if="bulkRequesting" small class="mr-1"/>
                            이력 없는 업체 전체 요청<template v-if="!jobsLoading && noHistoryTargets.length"> ({{ noHistoryTargets.length }})</template>
                        </b-button>
                        <b-button size="sm" variant="primary"
                                  :disabled="!evaluatingNoHistoryTargets.length || bulkRequesting || jobsLoading"
                                  @click="requestAllEvaluating">
                            <b-spinner v-if="bulkRequesting" small class="mr-1"/>
                            평가중 업체 전체 요청<template v-if="!jobsLoading && evaluatingNoHistoryTargets.length"> ({{ evaluatingNoHistoryTargets.length }})</template>
                        </b-button>
                        <b-button size="sm"
                                  :variant="selectMode ? 'secondary' : 'outline-secondary'"
                                  @click="toggleSelectMode">
                            {{ selectMode ? '선택 모드 종료' : '선택 요청' }}
                        </b-button>
                    </div>
                </div>
            </template>
            <div v-if="failedCampaignIds.length"
                 class="alert alert-warning d-flex align-items-center justify-content-between mb-0 rounded-0 py-2">
                <span class="small">
                    보완검토 이력을 불러오지 못한 캠페인이 있습니다. 해당 업체는 '확인 불가'로 표시되며 요청할 수 없습니다.
                </span>
                <b-button size="sm" variant="outline-secondary" :disabled="jobsLoading" @click="loadJobs()">다시 시도</b-button>
            </div>
            <!-- DS 표 규칙: 행 44 고정 · 폭은 머리가 정하고(업체 칸이 남는 폭) 넘치면 줄인다. 행 액션은 맨 끝 한 칸 -->
            <b-table
                responsive show-empty
                :items="pagedRows"
                :fields="fields"
                :busy="loading"
                :tbody-tr-class="rowClass"
                table-class="ds-table ds-table-fixed supplement-review-table"
                class="mb-0"
                :class="{'select-mode': selectMode}"
                v-on="selectMode ? {'row-clicked': onRowClicked} : {}"
            >
                <template #empty>
                    <div v-if="loadError" class="text-center py-5">
                        <div class="text-danger mb-2">
                            {{ loadError === 'projects' ? '프로젝트 목록을 불러오지 못했습니다.' : '업체 목록을 불러오지 못했습니다.' }}
                        </div>
                        <b-button size="sm" variant="outline-secondary" @click="retryLoad">다시 시도</b-button>
                    </div>
                    <!-- 대상 업체는 있는데 검색어에 맞는 업체가 없는 경우 — '대상 없음'과 구분 -->
                    <div v-else-if="companyFilter && eligibleRows.length" class="text-center text-muted py-5">
                        <div class="mb-2">「{{ companyFilter }}」 검색 결과가 없습니다.</div>
                        <b-button size="sm" variant="outline-secondary" @click="companyFilter = ''">검색어 지우기</b-button>
                    </div>
                    <div v-else class="text-center text-muted py-5">
                        {{ selectedProject
                            ? '이 프로젝트에는 보완 검토 대상(답변 제출) 업체가 없습니다.'
                            : '기간과 프로젝트를 선택하면 업체 목록이 표시됩니다.' }}
                    </div>
                </template>
                <template #table-busy>
                    <div class="text-center text-primary py-5">
                        <b-spinner class="align-middle mr-2"/>업체 목록을 불러오는 중...
                    </div>
                </template>

                <template #cell(index)="row">
                    {{ (pagination.currentPage - 1) * pagination.perPage + row.index + 1 }}
                </template>
                <!-- 이력이 있거나 확인 불가면 업체명이 상세 링크(id 가 없으면 글자). 선택 모드의 행 클릭과 겹치지 않게 .stop -->
                <template #cell(company_name)="row">
                    <span class="ds-ellipsis"
                          :title="`${pick(row.item, 'company_name') || ''} (${pick(row.item, 'company_id')})${row.item.priority ? ' · 대체평가' : ''}`">
                        <span v-if="row.item.priority" class="ds-tag">대체</span> <router-link v-if="(jobFor(row.item) || isHistoryUnknown(row.item)) && detailRoute(row.item)" :to="detailRoute(row.item)" class="ds-detail-link" @click.native.stop>{{ pick(row.item, 'company_name') }}</router-link><template v-else>{{ pick(row.item, 'company_name') }}</template> <span class="ds-sub">{{ pick(row.item, 'company_id') }}</span>
                    </span>
                </template>
                <template #cell(vendor_code)="row">
                    <span class="ds-ellipsis" :title="pick(row.item, 'vendor_code')">{{ pick(row.item, 'vendor_code') || '-' }}</span>
                </template>
                <template #cell(status)="row">
                    <span class="ds-status" :class="`ds-status-${getProgressTone(row.item.priority ? row.item.priority : row.item)}`">{{ statusText(row.item) }}</span>
                </template>
                <template #head(selected)>
                    <!-- 현재 페이지(보이는 행) 전체 선택/해제 — 다른 페이지 선택은 유지 -->
                    <b-form-checkbox :checked="allPageSelected"
                                     :indeterminate="somePageSelected && !allPageSelected"
                                     :disabled="!pageSelectableKeys.length"
                                     title="현재 페이지 전체 선택/해제"
                                     @change="toggleSelectPage"/>
                </template>
                <template #cell(selected)="row">
                    <!-- @click.stop: 체크박스 클릭이 row-clicked 토글과 중복되지 않도록.
                         :key 는 페이지 전환 시 체크박스 인스턴스가 다른 행에 재사용되어
                         내부 상태(localChecked)가 이월되는 것을 차단한다 -->
                    <div @click.stop>
                        <b-form-checkbox :key="rowKey(row.item)"
                                         :checked="isRowSelected(row.item)"
                                         :disabled="!isSelectable(row.item)"
                                         :title="isSelectable(row.item) ? '' : '이미 이력이 있거나 이력을 확인할 수 없는 업체입니다'"
                                         @change="toggleRowSelected(row.item)"/>
                    </div>
                </template>
                <template #cell(review_status)="row">
                    <b-spinner v-if="jobsLoading" small variant="secondary"/>
                    <span v-else-if="jobFor(row.item)" class="ds-status"
                          :class="`ds-status-${jobStatusVariant(jobFor(row.item).status)}`">{{ jobStatusLabel(jobFor(row.item).status) }}</span>
                    <span v-else-if="isHistoryUnknown(row.item)" class="ds-status ds-status-light"
                          title="잡 이력 조회 상한(50건) 초과 또는 조회 실패로 이 업체의 이력 유무를 확인할 수 없습니다. 상세 보기에서 확인하세요.">확인 불가</span>
                    <span v-else class="ds-sub">-</span>
                </template>
                <!-- 행 액션(아이콘 28): 이력이 있거나 확인 불가면 상세, 이력이 없으면 요청 -->
                <template #cell(detail)="row">
                    <b-spinner v-if="jobsLoading" small variant="secondary" label="보완검토 이력 확인 중"/>
                    <!-- 이력 확인 불가 업체는 목록에서 바로 요청하지 않고 상세에서
                         실제 이력을 확인 후 처리 (중복 잡 생성 방지) -->
                    <template v-else-if="jobFor(row.item) || isHistoryUnknown(row.item)">
                        <router-link v-if="detailRoute(row.item)" :to="detailRoute(row.item)" class="ds-row-icon-btn"
                                     :aria-label="`${pick(row.item, 'company_name')} 보완검토 상세`" title="보완검토 상세"
                                     @click.native.stop>
                            <AppIcon name="chevron-right"/>
                        </router-link>
                        <!-- id 가 없으면 goDetail 이 '진입 불가'를 알린다 -->
                        <button v-else type="button" class="ds-row-icon-btn"
                                :aria-label="`${pick(row.item, 'company_name')} 보완검토 상세`" title="보완검토 상세"
                                @click.stop="goDetail(row.item)">
                            <AppIcon name="chevron-right"/>
                        </button>
                    </template>
                    <!-- 막힌 아이콘 버튼은 클릭을 통과시키므로(pointer-events) 감싸개가 받아 선택 모드 행 클릭으로 새지 않게 한다 -->
                    <span v-else class="d-inline-flex align-middle" @click.stop>
                        <button type="button" class="ds-row-icon-btn"
                                :disabled="isRequesting(row.item) || bulkRequesting"
                                :aria-label="`${pick(row.item, 'company_name')} 보완검토 요청`" :title="`${pick(row.item, 'company_name')} 보완검토 요청`"
                                @click.stop="requestOne(row.item)">
                            <b-spinner v-if="isRequesting(row.item)" small/>
                            <AppIcon v-else name="send"/>
                        </button>
                    </span>
                </template>
            </b-table>

            <!-- 선택 요청 모드 footer -->
            <div v-if="selectMode"
                 class="d-flex align-items-center justify-content-between flex-wrap p-2 border-top"
                 style="gap: .5rem">
                <span class="text-muted small">
                    행을 클릭하거나 체크박스로 요청할 업체를 선택하세요 — 선택 {{ selectedKeys.length }}개
                </span>
                <div class="d-flex align-items-center" style="gap: .5rem">
                    <b-button size="sm" variant="outline-secondary"
                              :disabled="!selectedKeys.length || bulkRequesting"
                              @click="selectedKeys = []">
                        선택 해제
                    </b-button>
                    <b-button size="sm" variant="primary"
                              :disabled="!selectedKeys.length || bulkRequesting || jobsLoading"
                              @click="requestSelected">
                        <b-spinner v-if="bulkRequesting" small class="mr-1"/>
                        선택 {{ selectedKeys.length }}개 요청
                    </b-button>
                </div>
            </div>

            <div v-if="filteredRows.length"
                 class="d-flex align-items-center justify-content-center p-2">
                <b-pagination
                    v-model="pagination.currentPage"
                    :total-rows="filteredRows.length"
                    :per-page="pagination.perPage"
                    first-number last-number align="fill" size="sm"
                    class="mb-0"
                />
                <b-form-select v-model="pagination.perPage" size="sm"
                               class="ml-3" style="width:100px">
                    <b-form-select-option v-for="o in perPageOptions" :key="o.value" :value="o.value">
                        {{ o.text }}
                    </b-form-select-option>
                </b-form-select>
            </div>
        </b-card>
    </b-container>
</template>

<script>
// AI 보완 검토 목록 — 프로젝트/업체 선택 후 각 업체의 보완검토 상세(SupplementReview.vue)로 진입.
// 로스터 소스는 캠페인 보고서와 동일한 /assessments/results (SSoT=nice). 미신청('미') 업체는 제외.
import AssessmentCommonMixin from '@shared_mixins/assessment.common.mixin.js';
import {getDefaultPagination, getPerPageOptions} from '@shared/components/admin/pages/Management/common-data';

const STATUS_LABEL = {
    '에': '에코바디스', '미': '미신청', '신': '신청완료', '답': '답변중',
    '평': '평가중', '보': '보완중', '완': '완료', '취': '취소',
};

// 2024 상반기부터 올해 하반기까지 — 새 회차가 생겨도 배포 없이 선택 가능. 기본값은 현재 반기.
const NOW = new Date();
const PERIODS = [];
for (let y = 2024; y <= NOW.getFullYear(); y += 1) PERIODS.push(`${y}_1H`, `${y}_2H`);
const DEFAULT_PERIOD = '2026_1H';  // 기존 기본값 유지 — 선택지만 자동 생성
const DEFAULT_PER_PAGE = 10;
const JOBS_POLL_MS = 15000;
// 상세로 갔다가 돌아올 때 스크롤·선택 모드를 되살리는 sessionStorage 키
const RETURN_STATE_KEY = 'supplement-review-list:return';

// URL query 비교 — 값이 배열(?a=1&a=2)이면 문자열과 달라 다른 query 로 본다
function sameQuery(a, b) {
    const ka = Object.keys(a);
    return ka.length === Object.keys(b).length && ka.every((k) => a[k] === b[k]);
}

export default {
    name: 'SupplementReviewList',
    mixins: [AssessmentCommonMixin],
    data() {
        return {
            periods: PERIODS,
            selectedPeriod: DEFAULT_PERIOD,
            projectOptions: [],
            selectedProject: null,
            companyFilter: '',
            rows: [],
            loading: false,
            // 뒤로가기 안전: 초기 복원 중에는 상태 변경이 query 를 다시 쓰지 않도록 억제
            initializing: true,
            queryChangedWhileInit: false,  // 초기화 중 사이드바 재클릭 등 — 끝나고 최신 query 로 다시 맞춘다
            pagination: getDefaultPagination(1, DEFAULT_PER_PAGE),
            perPageOptions: getPerPageOptions(),
            // ── 보완검토 잡 이력 / 요청 상태 ──
            jobsByKey: {},        // `${campaign_id}:${company_id}` → 그 업체의 최신 잡
            jobsLoading: false,
            jobsLoadToken: 0,     // 프로젝트 전환 후 늦게 도착한 이력 응답 무시용
            // 요청으로 맵에 직접 넣은 순번(행 키별) — 그 전에 보낸 이력 조회 응답이 덮지 않도록
            jobsWriteSeq: 0,
            jobsWriteAt: {},
            jobsUpdatedAt: '',    // 이력을 마지막으로 받은 시각 HH:MM
            jobsPollTimer: null,
            jobsPolling: false,   // 자동 갱신 조회가 날아가 있음 — 응답이 15초보다 느려도 겹치지 않게
            // 잡 이력 50건 캡에 걸린 캠페인 — 맵에 없는 업체를 "이력 없음"으로
            // 단정할 수 없으므로(완료 이력이 잘렸을 수 있음) 별도로 추적한다
            truncatedCampaignIds: [],
            failedCampaignIds: [],  // 이력 조회가 실패한 캠페인 — 이력 확인 불가로 취급
            loadError: '',          // '' | 'projects' | 'roster' — 목록 조회 실패 (빈 목록과 구분)
            requestingKeys: [],   // 개별 요청 전송 중인 행 키 (버튼 spinner/disabled)
            bulkRequesting: false,
            selectMode: false,
            selectedKeys: [],     // 선택 요청 모드에서 체크된 행 키 — query 에는 넣지 않음
        };
    },
    computed: {
        // 보완 검토 대상 — 미신청('미') 제외
        eligibleRows() {
            return this.rows.filter((c) => this.getProgressStatus(c.priority ? c.priority : c) !== '미');
        },
        // 대상 + 업체명/코드 검색
        filteredRows() {
            const q = (this.companyFilter || '').trim().toLowerCase();
            if (!q) return this.eligibleRows;
            return this.eligibleRows.filter((c) => {
                const name = String(this.pick(c, 'company_name') || '').toLowerCase();
                const code = String(this.pick(c, 'vendor_code') || '').toLowerCase();
                return name.includes(q) || code.includes(q);
            });
        },
        hasActiveJobs() {
            return Object.values(this.jobsByKey).some((job) => this.isActiveJob(job));
        },
        pagedRows() {
            const start = (this.pagination.currentPage - 1) * this.pagination.perPage;
            return this.filteredRows.slice(start, start + this.pagination.perPage);
        },
        fields() {
            // 폭은 머리가 정한다(ds-table-fixed) — 폭을 안 적은 업체명 칸이 남는 폭을 갖고 넘치면 줄인다
            const cols = [
                {key: 'index', label: 'No.', thClass: 'ds-cell-no', tdClass: 'ds-cell-no', thStyle: {width: '56px'}},
                {key: 'company_name', label: '업체명'},
                {key: 'vendor_code', label: '업체코드', thStyle: {width: '130px'}},
                {key: 'status', label: '진행 상태', thStyle: {width: '104px'}},
                {key: 'review_status', label: '보완검토 상태', thStyle: {width: '120px'}},
                {key: 'detail', label: '동작', thClass: 'text-right', tdClass: 'text-right', thStyle: {width: '64px'}},
            ];
            if (this.selectMode) {
                cols.unshift({key: 'selected', label: '', thStyle: {width: '40px'},
                    thClass: ['text-center', 'select-cell'], tdClass: ['text-center', 'select-cell']});
            }
            return cols;
        },
        // 이력이 전혀 없어 바로 요청 가능한 업체 — 현재 필터(filteredRows) 기준.
        // 이력 확인 불가(50건 캡) 업체는 중복 잡 생성을 막기 위해 제외한다.
        noHistoryTargets() {
            return this.filteredRows.filter((c) => !this.jobFor(c)
                && !this.isHistoryUnknown(c)
                && this.hasValidIds(c));
        },
        // 평가중('평' = in_assessment) + 이력 없음 — "평가중 업체 전체 요청" 버튼 대상.
        // noHistoryTargets(이력/확인불가/유효id 안전조건) 위에 진행 상태 조건만 얹는다.
        evaluatingNoHistoryTargets() {
            return this.noHistoryTargets.filter((c) =>
                this.getProgressStatus(c.priority ? c.priority : c) === '평');
        },
        hasTruncatedHistory() {
            return this.truncatedCampaignIds.length > 0;
        },
        // ── 현재 페이지 전체 선택 (헤더 체크박스) ──
        pageSelectableKeys() {
            return this.pagedRows.filter((c) => this.isSelectable(c))
                .map((c) => this.rowKey(c));
        },
        allPageSelected() {
            return this.pageSelectableKeys.length > 0
                && this.pageSelectableKeys.every((k) => this.selectedKeys.includes(k));
        },
        somePageSelected() {
            return this.pageSelectableKeys.some((k) => this.selectedKeys.includes(k));
        },
    },
    watch: {
        // 필터/페이지 변경을 query 에 반영 (뒤로가기 시 복원). 초기 복원 중엔 억제.
        companyFilter() {
            if (this.initializing) return;
            this.pagination.currentPage = 1;
            this.syncQuery();
        },
        'pagination.currentPage'() {
            if (this.initializing) return;
            this.syncQuery();
        },
        'pagination.perPage'() {
            if (this.initializing) return;
            this.pagination.currentPage = 1;
            this.syncQuery();
        },
        // 같은 화면에서 query 만 바뀜(사이드바 재클릭 등) → 화면을 URL 대로 다시 맞춘다.
        // syncQuery 가 쓴 replace 는 현재 상태와 같아서 무시된다(루프 없음).
        '$route.query'(q) {
            if (this.initializing) {
                this.queryChangedWhileInit = true;
                return;
            }
            if (!sameQuery(q, this.buildQuery())) this.initFromQuery();
        },
        // 진행 중 잡이 있는 동안만 이력을 주기적으로 다시 받는다
        hasActiveJobs(on) {
            clearInterval(this.jobsPollTimer);
            this.jobsPollTimer = on ? setInterval(this.pollJobs, JOBS_POLL_MS) : null;
        },
    },
    created() {
        this.initFromQuery();
    },
    beforeDestroy() {
        clearInterval(this.jobsPollTimer);
    },
    beforeRouteLeave(to, from, next) {
        // 상세를 보고 뒤로 돌아오면 보던 위치·선택을 되살린다(initFromQuery 가 한 번 읽고 지운다)
        try {
            window.sessionStorage.setItem(RETURN_STATE_KEY, JSON.stringify({
                fullPath: from.fullPath,
                y: window.scrollY,
                selectMode: this.selectMode,
                selectedKeys: this.selectedKeys,
            }));
        } catch (e) { /* 저장 불가(사생활 보호 모드 등) — 복원만 안 된다 */ }
        next();
    },
    methods: {
        // ── query-parameter 로 랜딩 상태 복원 (뒤로가기 안전) ──────────
        async initFromQuery() {
            this.initializing = true;
            this.queryChangedWhileInit = false;
            const q = this.$route.query || {};
            // 크래프트된 링크로 관리자에게 보내는 쿼리는 신뢰할 수 없다. vue-router 는
            // 중복 키(?company=a&company=b)를 배열로 파싱하는데, 그 값이 companyFilter 로
            // 들어가면 filteredRows/syncQuery 의 .trim() 이 TypeError 를 던져 목록이 통째로
            // 렌더 불가가 된다. 모든 값을 스칼라로 강제하고 화이트리스트/범위로 검증한다.
            const first = (v) => (Array.isArray(v) ? v[0] : v);
            const asStr = (v) => (typeof first(v) === 'string' ? first(v) : '');
            // 없는 키는 기본값 — 사이드바 재클릭(빈 query)이면 처음 화면으로 돌아간다
            const period = asStr(q.period);
            this.selectedPeriod = this.periods.includes(period) ? period : DEFAULT_PERIOD;
            this.companyFilter = asStr(q.company).slice(0, 200);
            const perPage = Math.floor(Number(first(q.per_page)));
            this.pagination.perPage = this.perPageOptions.some((o) => o.value === perPage)
                ? perPage : DEFAULT_PER_PAGE;
            this.selectMode = false;
            const pageNum = Math.floor(Number(first(q.page)));
            const pendingPage = Number.isFinite(pageNum) && pageNum >= 1 ? pageNum : 1;
            const pendingProjectCode = asStr(q.project_code) || null;
            const returnState = this.takeReturnState();
            try {
                await this.loadProjects();
                if (pendingProjectCode) {
                    const proj = this.projectOptions.find(
                        (p) => p.project_code === pendingProjectCode);
                    if (proj) {
                        this.selectedProject = proj;
                        await this.onProjectChange();  // 로스터 로드 (currentPage=1 리셋)
                    } else if (!this.loadError) {
                        this.$EventBus.$emit('toast-warning', 'AI 보완 검토',
                            `링크의 프로젝트(${pendingProjectCode})를 이 기간 목록에서 찾을 수 없습니다. 기간을 확인해 주세요.`);
                    }
                }
                // 로스터 로드 후 저장돼 있던 페이지로 복원
                this.pagination.currentPage = pendingPage;
                if (returnState && this.rows.length) this.applyReturnState(returnState);
            } finally {
                this.initializing = false;
            }
            // 같은 틱에 다시 시작해야 위에서 바꾼 페이지 watcher 가 옛 상태로 syncQuery 하지 않는다
            if (this.queryChangedWhileInit && !sameQuery(this.$route.query, this.buildQuery())) {
                await this.initFromQuery();
            }
        },
        // 상세에서 돌아온 같은 URL 이면 떠날 때 저장한 상태를 돌려주고, 어느 경우든 지운다
        takeReturnState() {
            try {
                const saved = JSON.parse(window.sessionStorage.getItem(RETURN_STATE_KEY) || 'null');
                window.sessionStorage.removeItem(RETURN_STATE_KEY);
                return saved && saved.fullPath === this.$route.fullPath ? saved : null;
            } catch (e) {
                return null;
            }
        },
        applyReturnState(saved) {
            this.selectMode = !!saved.selectMode;
            if (this.selectMode && Array.isArray(saved.selectedKeys)) {
                this.selectedKeys = saved.selectedKeys;
                this.pruneSelectedKeys();  // 이력 로드가 끝나면 loadJobs 가 한 번 더 거른다
            }
            // 표가 그려진 뒤에 스크롤 — 라우터의 복원은 목록이 비어 있던 시점이라 맨 위에 머문다
            const y = Number(saved.y) || 0;
            this.$nextTick(() => window.requestAnimationFrame(() => window.scrollTo(0, y)));
        },
        buildQuery() {
            const query = {};
            if (this.selectedPeriod) query.period = this.selectedPeriod;
            if (this.selectedProject) query.project_code = this.selectedProject.project_code;
            const f = (this.companyFilter || '').trim();
            if (f) query.company = f;
            if (this.pagination.currentPage > 1) query.page = String(this.pagination.currentPage);
            if (this.pagination.perPage !== DEFAULT_PER_PAGE) query.per_page = String(this.pagination.perPage);
            return query;
        },
        syncQuery() {
            // 현재 상태를 URL query 로 replace — 히스토리 스택을 늘리지 않고
            // 현재 목록 URL 을 갱신해, 상세로 push 후 뒤로가기 시 그대로 복원된다.
            this.$router.replace({query: this.buildQuery()}).catch(() => {});
        },
        // 대체평가(priority) 우선 필드 선택
        pick(company, key) {
            return company.priority && company.priority[key] != null
                ? company.priority[key] : company[key];
        },
        statusText(company) {
            return STATUS_LABEL[this.getProgressStatus(company.priority ? company.priority : company)] || '-';
        },
        async onPeriodChange() {
            this.pagination.currentPage = 1;
            await this.loadProjects();  // selectedProject/rows 리셋
            this.syncQuery();
        },
        async loadProjects() {
            this.loading = true;
            this.selectedProject = null;
            this.projectOptions = [];
            this.rows = [];
            this.loadError = '';
            this.resetJobsState();
            try {
                // /projects 는 per_page 상한(≤100)이 있어 페이지를 끝까지 수집한다 —
                // 한 기간에 프로젝트가 100개를 넘으면 뒤쪽이 목록에서 누락되지 않도록.
                // 종료 조건은 API 메타(pages/total). 임의 페이지 상한 없이, 빈 페이지
                // 응답을 무한루프 backstop 으로 둔다(잘못된 pages 메타 방어).
                const PER_PAGE = 100;
                const items = [];
                let page = 1;
                let pages = 1;
                let total = Infinity;
                for (;;) {
                    const {data} = await this.$axios.get('/projects', {
                        params: {period: this.selectedPeriod, page, per_page: PER_PAGE},
                    });
                    const pageItems = (data && data.items) || [];
                    items.push(...pageItems);
                    pages = (data && data.pages) || page;
                    if (data && typeof data.total === 'number') total = data.total;
                    // 빈 페이지 / 전량 수집 / 마지막 페이지 → 종료 (backstop 포함)
                    if (pageItems.length === 0 || items.length >= total || page >= pages) {
                        break;
                    }
                    page += 1;
                }

                // project_code 중복 제거(캠페인 1:1 레거시) + 이름순
                const seen = new Set();
                this.projectOptions = items
                    .filter((it) => {
                        if (!it.project_code || seen.has(it.project_code)) return false;
                        seen.add(it.project_code);
                        return true;
                    })
                    .map((it) => ({
                        project_code: it.project_code,
                        client: it.client,
                        campaign: it.campaign,
                    }))
                    .sort((a, b) => String(a.project_code).localeCompare(String(b.project_code)));
            } catch (e) {
                this.loadError = 'projects';
                this.$EventBus.$emit('toast-failure', '조회 오류', '프로젝트 목록을 불러오지 못했습니다.');
            } finally {
                this.loading = false;
            }
        },
        async onProjectChange() {
            if (!this.selectedProject) return;
            this.loading = true;
            this.rows = [];
            this.loadError = '';
            this.pagination.currentPage = 1;
            this.resetJobsState();
            try {
                // admin_mode=project 로스터 — project_code 기준 전체 업체
                const {data} = await this.$axios.get('/assessments/results', {
                    params: {project_code: this.selectedProject.project_code},
                });
                this.rows = this.sortingCompanies(Array.isArray(data) ? data : [], {});
            } catch (e) {
                this.loadError = 'roster';
                this.$EventBus.$emit('toast-failure', '조회 오류', '업체 목록을 불러오지 못했습니다.');
            } finally {
                this.loading = false;
            }
            if (!this.initializing) this.syncQuery();
            // 로스터 표시를 막지 않도록 이력 로드는 비동기로 (에러는 내부 처리)
            this.loadJobs();
        },
        retryLoad() {
            return this.loadError === 'projects' ? this.loadProjects() : this.onProjectChange();
        },
        // 상세(SupplementReview) route — id 가 없으면 null (목록 링크 · goDetail 공용)
        detailRoute(company) {
            // 대체평가 우선 (campaign_report goSupplementReview 와 동일)
            const campaign_id = this.pick(company, 'campaign_id');
            const company_id = this.pick(company, 'company_id');
            if (!campaign_id || !company_id) return null;
            // job_id: 상세가 이 회차를 연다(다른 업체 잡이면 상세가 최신 잡으로 바꾼다)
            const job = this.jobFor(company);
            const query = {campaign_id, company_id};
            if (job && job.job_id) query.job_id = String(job.job_id);
            return {name: 'ManagingSupplementReview', query};
        },
        goDetail(company) {
            const route = this.detailRoute(company);
            if (!route) {
                this.$EventBus.$emit('toast-failure', '진입 불가',
                    '이 업체의 campaign_id / company_id 를 찾을 수 없습니다.');
                return;
            }
            this.$router.push(route).catch(() => {});
        },
        // ── 보완검토 잡 이력 맵 ──────────────────────────────────────
        rowKey(company) {
            return `${this.pick(company, 'campaign_id')}:${this.pick(company, 'company_id')}`;
        },
        hasValidIds(company) {
            return this.pick(company, 'campaign_id') != null
                && this.pick(company, 'company_id') != null;
        },
        jobFor(company) {
            return this.jobsByKey[this.rowKey(company)] || null;
        },
        // 이력 유무 판단 불가 — 캠페인 잡 이력이 50건 캡에 걸렸고 맵에도 없는 업체
        isHistoryUnknown(company) {
            const id = this.pick(company, 'campaign_id');
            return !this.jobFor(company)
                && (this.truncatedCampaignIds.includes(id) || this.failedCampaignIds.includes(id));
        },
        // 선택 요청 대상 — 이력이 있거나 확인 불가인 업체는 중복 AI 판정을 막기 위해 제외
        isSelectable(company) {
            return this.hasValidIds(company) && !this.jobFor(company) && !this.isHistoryUnknown(company);
        },
        isActiveJob(job) {
            // 상세(SupplementReview.vue isJobActive)와 동일 기준
            return !!job && ['PENDING', 'RUNNING', 'FINALIZING'].includes(job.status);
        },
        isRequesting(company) {
            return this.requestingKeys.includes(this.rowKey(company));
        },
        resetJobsState() {
            this.jobsLoadToken += 1;  // 진행 중이던 이전 프로젝트 이력 응답 무시
            this.jobsByKey = {};
            this.truncatedCampaignIds = [];
            this.failedCampaignIds = [];
            this.selectedKeys = [];
            this.jobsLoading = false;
            this.jobsUpdatedAt = '';
            this.jobsPolling = false;
        },
        // 요청 직후 맵에 진행 중 잡을 넣는다(이력 재조회 없이 바로 '진행 중' 표시)
        setLocalJob(campaign_id, company_id, job_id) {
            const key = `${campaign_id}:${company_id}`;
            this.jobsWriteSeq += 1;
            this.jobsWriteAt[key] = this.jobsWriteSeq;
            this.$set(this.jobsByKey, key, {job_id, campaign_id, company_id, status: 'PENDING'});
        },
        pruneSelectedKeys() {
            const ok = new Set(this.rows.filter((c) => this.isSelectable(c)).map((c) => this.rowKey(c)));
            this.selectedKeys = this.selectedKeys.filter((k) => ok.has(k));
        },
        async pollJobs() {
            // 창이 가려져 있거나 이미 조회 중이면 이번 틱은 건너뛴다
            if (document.hidden || this.jobsLoading || this.jobsPolling) return;
            this.jobsPolling = true;
            try {
                await this.loadJobs({silent: true});
            } finally {
                this.jobsPolling = false;
            }
        },
        // silent: 자동 갱신 — 배지를 스피너로 바꾸지 않고, 실패하면 이전 상태를 그대로 두고 알리지 않는다
        async loadJobs({silent = false} = {}) {
            // 로스터의 distinct campaign_id 별로 GET /supplement-review/jobs 를 호출해
            // 업체별 최신 잡 맵을 만든다 (응답은 최신순 → 업체당 첫 잡이 최신).
            // ⚠️ 캠페인당 최대 50건(잡 단위) 제한 — 캡에 걸린 캠페인은 맵에 없는 업체를
            // "이력 없음"으로 단정할 수 없다. 서버 409/skipped 안전망은 "활성" 잡에만
            // 성립하고 완료(DONE 등) 이력 업체에 다시 POST 하면 새 잡이 생기므로,
            // 그런 업체는 truncatedCampaignIds 로 표시해 일괄 요청 대상에서 제외한다.
            const JOBS_PAGE_CAP = 50;
            const campaignIds = [...new Set(
                this.rows.map((c) => this.pick(c, 'campaign_id')).filter((id) => id != null))];
            if (!campaignIds.length) return;
            const token = ++this.jobsLoadToken;
            const writeSeq = this.jobsWriteSeq;
            if (!silent) this.jobsLoading = true;
            try {
                // 캠페인별로 분리 — 실패한 캠페인만 '확인 불가'로 취급 (이력 없음으로 오인 방지)
                const settled = await Promise.allSettled(campaignIds.map((id) =>
                    this.$axios.get('/supplement-review/jobs', {params: {campaign_id: id}})
                        .then(({data}) => (Array.isArray(data) ? data : []))));
                if (token !== this.jobsLoadToken) return;
                // 세션 만료 — 인터셉터가 로그인으로 보낸다
                if (settled.some((r) => r.status !== 'fulfilled'
                    && r.reason && r.reason.response && r.reason.response.status === 401)) return;
                const map = {};
                const truncated = [];
                const failed = [];
                settled.forEach((r, idx) => {
                    if (r.status !== 'fulfilled') {
                        failed.push(campaignIds[idx]);
                        return;
                    }
                    const jobs = r.value;
                    if (jobs.length >= JOBS_PAGE_CAP) truncated.push(campaignIds[idx]);
                    jobs.forEach((job) => {
                        const key = `${job.campaign_id}:${job.company_id}`;
                        if (!map[key]) map[key] = job;
                    });
                });
                if (silent && failed.length) return;
                // 조회 중에 요청해 맵에 넣은 잡은 응답이 그보다 먼저 만들어졌을 수 있어 로컬 값을 남긴다
                Object.keys(this.jobsByKey).forEach((k) => {
                    if ((this.jobsWriteAt[k] || 0) > writeSeq) map[k] = this.jobsByKey[k];
                });
                this.jobsByKey = map;
                this.truncatedCampaignIds = truncated;
                this.failedCampaignIds = failed;
                this.jobsUpdatedAt = new Date().toTimeString().slice(0, 5);
                this.pruneSelectedKeys();
                if (failed.length) {
                    this.$EventBus.$emit('toast-failure', '조회 오류',
                        '일부 캠페인의 보완검토 이력을 불러오지 못했습니다.');
                }
            } finally {
                if (token === this.jobsLoadToken) this.jobsLoading = false;
            }
        },
        jobStatusLabel(status) {
            return {
                PENDING: '진행 중', RUNNING: '진행 중', FINALIZING: '진행 중',
                DONE: '완료', PARTIAL: '부분 완료', FAILED: '실패',
            }[status] || status || '-';
        },
        jobStatusVariant(status) {
            // 상세(SupplementReview.vue jobStatusVariant)와 동일 팔레트
            return {DONE: 'success', PARTIAL: 'warning', FAILED: 'danger'}[status] || 'info';
        },
        // 400/409/429 바디는 plain text 와 {message} JSON 이 혼재 — 방어적으로 추출
        errorMessage(res, fallback) {
            const d = res && res.data;
            if (d && typeof d === 'object' && d.message) return d.message;
            if (typeof d === 'string' && d.trim() && !/[<>]/.test(d)) {
                return d.trim().slice(0, 200);
            }
            return fallback;
        },
        // ── 개별 요청 (이력 없는 행의 버튼) ──────────────────────────
        async requestOne(company) {
            const campaign_id = this.pick(company, 'campaign_id');
            const company_id = this.pick(company, 'company_id');
            if (!campaign_id || !company_id) {
                this.$EventBus.$emit('toast-failure', '요청 불가',
                    '이 업체의 campaign_id / company_id 를 찾을 수 없습니다.');
                return;
            }
            const key = `${campaign_id}:${company_id}`;
            if (this.requestingKeys.includes(key)) return;
            this.requestingKeys.push(key);
            try {
                const {data} = await this.$axios.post('/supplement-review',
                    {campaign_id, company_id});
                this.setLocalJob(campaign_id, company_id, data.job_id);
                this.$EventBus.$emit('toast-success', '보완검토 요청',
                    `${this.pick(company, 'company_name') || company_id} 보완검토 요청을 전송했습니다.`);
            } catch (e) {
                const res = e.response || {};
                if (res.status === 401) return;
                if (res.status === 409 && res.data && res.data.job_id) {
                    // 이미 진행 중 잡 존재 → 그 잡으로 맵 갱신 (이력 있음으로 전환)
                    this.setLocalJob(campaign_id, company_id, res.data.job_id);
                    this.$EventBus.$emit('toast-failure', '이미 진행 중',
                        '이미 진행 중인 보완검토가 있습니다. "진행 중" 버튼으로 확인하세요.');
                } else if (res.status === 429) {
                    // 서버가 실제 상한 안내를 주면 우선 사용 (상한값 하드코딩 드리프트 방지)
                    this.$EventBus.$emit('toast-failure', '요청 제한',
                        this.errorMessage(res,
                            '동시 진행 상한(3건)에 도달했습니다. 잠시 후 다시 시도하세요.'));
                } else {
                    this.$EventBus.$emit('toast-failure', '요청 실패',
                        this.errorMessage(res, '보완검토 요청에 실패했습니다.'));
                }
            } finally {
                this.requestingKeys = this.requestingKeys.filter((k) => k !== key);
            }
        },
        // ── 일괄/선택 요청 — 일괄 API 1회 호출 ───────────────────────
        async requestBulk(items) {
            if (!items.length || this.bulkRequesting) return false;
            this.bulkRequesting = true;
            try {
                const {data} = await this.$axios.post('/supplement-review/bulk', {items});
                const accepted = (data && data.accepted) || [];
                const skipped = (data && data.skipped) || [];
                accepted.forEach((a) => this.setLocalJob(a.campaign_id, a.company_id, a.job_id));
                this.$EventBus.$emit('toast-success', '일괄 보완검토 요청',
                    `${accepted.length}건 요청${skipped.length ? `, ${skipped.length}건 건너뜀` : ''}`);
                // skipped(already_active 등) 행의 실제 잡 상태를 맵에 반영
                if (skipped.length) this.loadJobs();
                return true;
            } catch (e) {
                const res = e.response || {};
                // 일괄 요청 엔드포인트(/supplement-review/bulk) 미배포 방어.
                // cross-origin 환경에서는 미배포 경로가 CORS preflight 실패로 떨어져
                // e.response 자체가 없으므로(status 판독 불가) 무응답도 같은 분기로 처리.
                if (res.status === 404 || !e.response) {
                    this.$EventBus.$emit('toast-failure', '일괄 요청 사용 불가',
                        '일괄 요청이 아직 준비되지 않았거나 연결에 실패했습니다. 개별 "보완검토 요청" 버튼을 이용해주세요.');
                } else {
                    this.$EventBus.$emit('toast-failure', '일괄 요청 실패',
                        this.errorMessage(res, '일괄 보완검토 요청에 실패했습니다.'));
                }
                return false;
            } finally {
                this.bulkRequesting = false;
            }
        },
        // 비용이 드는 일괄 발행 — 대상 건수·업체명을 보여주고 승인 시에만 요청
        async confirmAndRequest(companies, label) {
            if (!companies.length) return false;
            const h = this.$createElement;
            const names = companies.slice(0, 5).map((c) => this.pick(c, 'company_name') || this.pick(c, 'company_id'));
            const ok = await this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: `${label} (${companies.length}곳)`,
                message: h('div', {}, [
                    h('div', {}, `프로젝트 ${this.selectedProject ? this.selectedProject.project_code : '-'}의 업체 ${companies.length}곳에 AI 보완검토를 요청합니다. 업체 수만큼 AI 비용이 발생하며 요청 후에는 취소할 수 없습니다.`),
                    h('div', {class: 'text-muted small mt-2'},
                        `대상: ${names.join(', ')}${companies.length > names.length ? ` 외 ${companies.length - names.length}곳` : ''}`),
                ]),
            });
            if (!ok) return false;
            return this.requestBulk(companies.map((c) => ({
                campaign_id: this.pick(c, 'campaign_id'),
                company_id: this.pick(c, 'company_id'),
            })));
        },
        requestAllNoHistory() {
            return this.confirmAndRequest(this.noHistoryTargets, '이력 없는 업체 전체 요청');
        },
        requestAllEvaluating() {
            return this.confirmAndRequest(this.evaluatingNoHistoryTargets, '평가중 업체 전체 요청');
        },
        async requestSelected() {
            // 전송 시점 스냅샷 — 배치 in-flight 중 새로 체크한 선택은 보존한다
            const sent = [...this.selectedKeys];
            const companies = this.rows.filter((c) => sent.includes(this.rowKey(c)) && this.isSelectable(c));
            const ok = await this.confirmAndRequest(companies, '선택 업체 요청');
            if (ok) {
                this.selectedKeys = this.selectedKeys.filter((k) => !sent.includes(k));
            }
        },
        // ── 선택 요청 모드 ───────────────────────────────────────────
        toggleSelectMode() {
            this.selectMode = !this.selectMode;
            this.selectedKeys = [];
        },
        isRowSelected(company) {
            return this.selectedKeys.includes(this.rowKey(company));
        },
        toggleRowSelected(company) {
            if (!this.isSelectable(company)) return;
            const key = this.rowKey(company);
            this.selectedKeys = this.selectedKeys.includes(key)
                ? this.selectedKeys.filter((k) => k !== key)
                : [...this.selectedKeys, key];
        },
        toggleSelectPage() {
            // 현재 페이지 행만 대상으로 토글 — 다른 페이지에서 체크한 선택은 유지
            const pageKeys = this.pageSelectableKeys;
            if (this.allPageSelected) {
                this.selectedKeys = this.selectedKeys.filter((k) => !pageKeys.includes(k));
            } else {
                this.selectedKeys = [...new Set([...this.selectedKeys, ...pageKeys])];
            }
        },
        onRowClicked(item) {
            // 선택 모드에서 행 아무 곳이나 클릭해도 토글 (버튼/체크박스 클릭은 제외됨)
            if (!this.selectMode) return;
            this.toggleRowSelected(item);
        },
        rowClass(item, type) {
            if (type !== 'row' || !item || !this.selectMode) return '';
            return this.isRowSelected(item) ? 'table-primary' : '';
        },
    },
};
</script>

<style scoped>
#supplement-review-list-page .list-container {
    box-shadow: 0 0 0.25rem rgba(0, 0, 0, 0.05);
}

/* 고정 열 합(No. 56 · 업체코드 130 · 진행 상태 104 · 보완검토 상태 120 · 동작 64 · 선택 40 = 514) + 업체 칸 ~246 — 좁으면 가로 스크롤 */
#supplement-review-list-page ::v-deep .supplement-review-table {
    min-width: 760px;
}

/* 선택 칸(40)은 좌우 여백을 8 로 — 공통 16 이면 체크박스(24)가 칸을 넘친다 */
#supplement-review-list-page ::v-deep .supplement-review-table .select-cell {
    padding-left: 8px;
    padding-right: 8px;
}

/* 선택 요청 모드: 행 전체가 클릭 대상임을 커서로 표시 */
#supplement-review-list-page .select-mode ::v-deep tbody tr {
    cursor: pointer;
}
</style>
